/**
 * 手写签名组件 - SignaturePad
 *
 * 功能：
 * - 支持手写签名输入
 * - 清除签名功能
 * - 导出Base64签名数据
 * - 响应式Canvas尺寸
 */

import React, { useRef, useEffect, useState } from 'react';
import { Button, Space, Alert } from 'antd';
import { ClearOutlined, CheckOutlined } from '@ant-design/icons';
import SignatureCanvas from 'react-signature-canvas';

interface SignaturePadProps {
  onConfirm: (signatureData: string) => void;
  onClear?: () => void;
  width?: number;
  height?: number;
  disabled?: boolean;
  defaultSignature?: string; // 已有签名（Base64）
}

const SignaturePad: React.FC<SignaturePadProps> = ({
  onConfirm,
  onClear,
  width = 600,
  height = 200,
  disabled = false,
  defaultSignature,
}) => {
  const sigCanvas = useRef<SignatureCanvas>(null);
  const [isEmpty, setIsEmpty] = useState(true);

  // 加载默认签名
  useEffect(() => {
    if (defaultSignature && sigCanvas.current) {
      sigCanvas.current.fromDataURL(defaultSignature);
      setIsEmpty(false);
    }
  }, [defaultSignature]);

  // 清除签名
  const handleClear = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
      setIsEmpty(true);
      onClear?.();
    }
  };

  // 确认签名
  const handleConfirm = () => {
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      const dataURL = sigCanvas.current.toDataURL('image/png');
      onConfirm(dataURL);
    }
  };

  // 监听签名变化
  const handleEnd = () => {
    if (sigCanvas.current) {
      setIsEmpty(sigCanvas.current.isEmpty());
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <Alert
        message="签名提示"
        description='请在下方区域内使用鼠标或触摸屏书写您的签名。签名完成后点击"确认签名"按钮。'
        type="info"
        showIcon
      />

      <div
        style={{
          border: '2px dashed #d9d9d9',
          borderRadius: '8px',
          padding: '16px',
          background: disabled ? '#f5f5f5' : '#ffffff',
          cursor: disabled ? 'not-allowed' : 'crosshair',
        }}
      >
        <SignatureCanvas
          ref={sigCanvas}
          canvasProps={{
            width,
            height,
            className: 'signature-canvas',
            style: {
              border: '1px solid #e8e8e8',
              borderRadius: '4px',
              background: '#ffffff',
            },
          }}
          onEnd={handleEnd}
          penColor="#000000"
          minWidth={1}
          maxWidth={3}
          velocityFilterWeight={0.7}
          dotSize={1}
        />
      </div>

      <Space>
        <Button
          icon={<ClearOutlined />}
          onClick={handleClear}
          disabled={disabled || isEmpty}
        >
          清除签名
        </Button>
        <Button
          type="primary"
          icon={<CheckOutlined />}
          onClick={handleConfirm}
          disabled={disabled || isEmpty}
        >
          确认签名
        </Button>
      </Space>

      {!isEmpty && (
        <Alert
          message="签名预览"
          description='请确认签名清晰可辨认后再点击"确认签名"按钮。'
          type="success"
          showIcon
        />
      )}
    </div>
  );
};

export default SignaturePad;
